import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getApiBase } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

const API_BASE = getApiBase();

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const isGuest = searchParams.get("guest") === "1";
  const coachCode = searchParams.get("coach_code") || "";

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let attempts = 0;
    const checkStatus = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/payments/status/${sessionId}`);
        setPaymentInfo(data);
        if (data.payment_status === "paid") {
          setStatus("success");
        } else if (attempts >= 10) {
          setStatus("pending");
        } else {
          attempts++;
          setTimeout(checkStatus, 2000);
        }
      } catch {
        if (attempts >= 3) setStatus("error");
        else { attempts++; setTimeout(checkStatus, 2000); }
      }
    };
    checkStatus();
  }, [sessionId]);

  const loginUrl = coachCode
    ? `/login?coach_code=${encodeURIComponent(coachCode)}`
    : "/login";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="payment-success-page">
      <Card className="border-slate-100 max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === "loading" && (
            <>
              <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
              <h2 className="font-heading text-2xl font-bold text-slate-900">Vérification du paiement...</h2>
              <p className="text-slate-500 mt-2">Veuillez patienter</p>
            </>
          )}
          {status === "success" && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="font-heading text-2xl font-bold text-slate-900">Paiement réussi !</h2>
              <p className="text-slate-500 mt-2">
                {isGuest
                  ? "Votre paiement est confirmé. Connectez-vous pour accéder à votre espace élève."
                  : "Votre paiement a été confirmé."}
              </p>
              {paymentInfo?.amount_total && (
                <p className="text-lg font-bold text-slate-900 mt-4">{(paymentInfo.amount_total / 100).toFixed(2)}€</p>
              )}
              {isGuest ? (
                <div className="mt-6 space-y-3">
                  <Button
                    onClick={() => navigate(loginUrl)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8"
                    data-testid="guest-login-btn"
                  >
                    Se connecter / Créer mon mot de passe
                  </Button>
                  <p className="text-xs text-slate-400">
                    Utilisez l&apos;email saisi lors du paiement. Mot de passe oublié ? Cliquez sur « Mot de passe oublié ».
                  </p>
                </div>
              ) : (
                <Button
                  onClick={() => navigate("/student/dashboard")}
                  className="mt-6 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8"
                  data-testid="back-to-dashboard-btn"
                >
                  Retour au dashboard
                </Button>
              )}
            </>
          )}
          {status === "pending" && (
            <>
              <Loader2 className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h2 className="font-heading text-2xl font-bold text-slate-900">Paiement en cours</h2>
              <p className="text-slate-500 mt-2">Votre paiement est en cours de traitement.</p>
              <Button
                onClick={() => navigate(isGuest ? loginUrl : "/student/payments")}
                className="mt-6 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8"
              >
                {isGuest ? "Se connecter" : "Voir mes paiements"}
              </Button>
            </>
          )}
          {status === "error" && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="font-heading text-2xl font-bold text-slate-900">Erreur de paiement</h2>
              <p className="text-slate-500 mt-2">Une erreur est survenue. Veuillez réessayer.</p>
              <Button
                onClick={() => navigate(isGuest ? "/" : "/student/payments")}
                className="mt-6 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8"
              >
                Retour
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}